import {
  FORM_VALID,
  type FieldName,
  type ValidationResult,
  type Validator,
  required,
} from '@ds/components/Form'
import type { InputValue } from '@ds/types/InputValue'

export const numberLowerThan =
  <Values extends object>(max: string, error: string): Validator<Values> =>
  (value) => {
    if (typeof value !== 'string') return error

    return parseFloat(value.replace(',', '.')) <= parseFloat(max)
      ? FORM_VALID
      : error
  }

export const numberGreaterThan =
  <Values extends object>(min: string, error: string): Validator<Values> =>
  (value) => {
    if (typeof value !== 'string') return error

    return parseFloat(value.replace(',', '.')) >= parseFloat(min)
      ? FORM_VALID
      : error
  }

export const isValidNumber =
  <Values extends object>(error: string): Validator<Values> =>
  (value) => {
    if (typeof value !== 'string') return error

    return Number.isNaN(Number(value.replace(',', '.'))) ? error : FORM_VALID
  }

export const formatAmount = (
  amount: InputValue,
  minimumFractionDigits?: number,
) => {
  if (typeof amount !== 'string') return amount?.toString()

  const sanitizedAmount = amount
    // Delete everything that is not a number, a comma or a dot
    .replace(/[^\d.,]/g, '')
    // Delete any comma or dot if it begins the number input
    .replace(/^(\.|,)/g, '')

  // Handle Currencies without decimals by removing any fraction digit and comma or dot
  if (minimumFractionDigits === 0) {
    return sanitizedAmount.replace(
      /^(?<amount>\d+)(?<separatorAndDecimals>[,.]\d*)$/,
      '$<amount>',
    )
  }

  // Otherwise, remove any extra fraction digit
  return sanitizedAmount.replace(
    /^(?<amount>\d+[,.]\d{2})(?<extraFractionDigits>\d+)$/,
    '$<amount>',
  )
}

/**
 * requiredIf
 *
 * Form validator that checks if a field is not empty only if an other field has a specific value
 * @param {string} fieldName - The name of the field that needs to have a specific value to make it required
 * @param {string} fieldValue - The value of the field making other fields required
 * @param {string} errorMessage – The error message to return if at least one value is not defined
 * @returns {string | 1} – An error message if check fails and one if it succeeded
 */
export const requiredIf = <Values extends object>(
  fieldName: FieldName<Values>,
  fieldValue: unknown,
  errorMessage: string,
): Validator<Values> => {
  /**
   * @param {value} value – The value of the current field
   * @param {object} values – All the values of the form
   */
  return (value, values: Values): ValidationResult => {
    const trimmedValue = typeof value === 'string' ? value.trim() : value

    if (values[fieldName] === fieldValue) {
      return required(errorMessage)(trimmedValue, values, {})
    }

    return FORM_VALID
  }
}
